import {
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    Directive,
    EventEmitter,
    Input,
    OnInit,
    Output,
    QueryList,
    TemplateRef
} from '@angular/core';
import {
    NgxDataSource,
    NgxDataSourcePage,
    NgxDataSourcePager,
    NgxDataSourceTrigger
} from '@ngx-component-kit/tables/tables.interfaces';
import {map, publish, refCount, switchMap} from 'rxjs/operators';
import {BehaviorSubject, Observable} from 'rxjs';

@Directive({
    selector: '[ngxTableColumnDef]'
})
export class NgxTableColumnDef {
}

@Component({
    selector: 'ngx-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false
})
export class NgxTable implements OnInit {

    @ContentChildren(NgxTableColumnDef, {read: TemplateRef}) tableColumnTemplates: QueryList<NgxTableColumnDef>;

    // Visual inputs.
    @Input() title: string | null = null;
    @Input() maxHeight = 'none';
    @Input() initialPageNumber = 1;
    @Input() initialPageSize = 25;

    // Placeholder inputs.
    @Input() searchPlaceholder: string | null = null;
    @Input() emptyPlaceholder: string | null = null;
    @Input() perPagePlaceholder = 'Items per page:';
    @Input() ofPlaceholder = 'of';

    // Data source inputs.
    @Input() identifier = 'id';
    @Input() queryFields = '';
    @Input() dataSource: NgxDataSource = null;

    // Outputs.
    @Output() onRowClicked = new EventEmitter<any>();

    // Observable data.
    page$: Observable<NgxDataSourcePager>;
    data$: Observable<NgxDataSourcePage<any>>;
    trigger$: BehaviorSubject<NgxDataSourceTrigger>;

    // Caching data.
    currentPage = -1;
    query = '';

    // Track by function.
    trackByFn = (index: number, item: any) => {
        return item[this.identifier];
    };

    get hasClickListeners() {
        return this.onRowClicked.observers.length > 0;
    }

    ngOnInit() {
        // Check if the data source exists.
        if (!this.dataSource) {
            throw Error('No data source was found.');
        }

        // Setup the trigger.
        this.setupTrigger();

        // Setup the data.
        this.setupData();

        // Setup the pager.
        this.setupPager();
    }

    onSearchEvent(newQueryValue: string) {
        // Get the current trigger.
        const trigger = this.trigger$.getValue();

        // Set the new trigger value.
        // Set the page number to 1.
        this.setTrigger({query: newQueryValue, pager: {pageNumber: 1, pageSize: trigger.pager.pageSize}});
    }

    onPageEvent(pager: NgxDataSourcePager) {
        // Check if the pager exists.
        if (!pager) {
            return;
        }

        // Set the new trigger value.
        this.setTrigger({pager});
    }

    onRowClickedEvent(item: any) {
        // Check if the developer is listening to the
        // click events.
        if (!this.hasClickListeners) {
            return;
        }

        // Emit a row clicked event.
        this.onRowClicked.emit(item);
    }

    update() {
        // Just set the trigger with the same value to
        // create a stream update.
        this.setTrigger(this.trigger$.getValue());
    }

    private setupTrigger() {
        // Create an initial pager.
        const initialPager = {pageNumber: this.initialPageNumber, pageSize: this.initialPageSize};

        // Create the trigger.
        this.trigger$ = new BehaviorSubject<NgxDataSourceTrigger>({
            pager: initialPager,
            queryFields: '',
            query: ''
        });
    }

    private setupData() {
        // Create the stream that will supply the data.
        this.data$ = this.trigger$.pipe(
            switchMap(trigger => this.makeRequest(trigger)),
            publish(),
            refCount()
        );
    }

    private setupPager() {
        // Create a stream so that the pager knows which page we are on.
        this.page$ = this.data$.pipe(
            map(({pageSize, pageNumber, totalItems}) => {
                this.currentPage = pageNumber;
                return {
                    pageSize,
                    pageNumber,
                    totalItems
                };
            })
        );
    }

    private makeRequest(trigger: NgxDataSourceTrigger) {
        // Request the dataSource for an observable page.
        return this.dataSource.onChange({
            pager: trigger.pager,
            queryFields: this.queryFields,
            query: trigger.query
        });
    }

    private setTrigger(newData: Partial<NgxDataSourceTrigger>) {
        // Grab the old trigger value.
        const oldValue = this.trigger$.getValue();

        // Push the new trigger value.
        this.trigger$.next({...oldValue, ...newData});
    }

}
